import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  AUDIT_PROMPT_KEY,
  RULES_PLACEHOLDER,
  getActivePrompt,
  isMissingRulesPlaceholder,
  savePrompt,
} from './operations';
import { Breadcrumbs, Button, Footer, SubHeader } from '../../components';

import './styles.scoped.css';

// CP-836 Slice 4 — AI Prompt Management (mockup page 24). Global-admin only (gated by verifyRoute +
// the Configurations section of the Admin Portal menu). Shows the current DQF AI Evaluation prompt
// read-only; Edit enables the field, Save writes a new active version (append-only — see operations).
const AiPromptManagement = () => {
  const navigate = useNavigate();

  const [current, setCurrent] = useState(null); // the active AiPrompt row
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prompt = await getActivePrompt(AUDIT_PROMPT_KEY);
      if (cancelled) return;
      setCurrent(prompt);
      setBody(prompt?.body || '');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    if (!body.trim()) {
      setError('The prompt cannot be empty.');
      return;
    }
    if (isMissingRulesPlaceholder(body)) {
      setError(
        `The prompt must include ${RULES_PLACEHOLDER} — the client's checklist is inserted ` +
          'there when an evaluation runs. Without it the evaluation runs with no client rules.',
      );
      return;
    }
    if (body.trim() === (current?.body || '').trim()) {
      setError('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      await savePrompt(AUDIT_PROMPT_KEY, body.trim(), current);
      const refreshed = await getActivePrompt(AUDIT_PROMPT_KEY);
      setCurrent(refreshed);
      setBody(refreshed?.body || '');
      setEditing(false);
    } catch (e) {
      console.log(e);
      setError('Could not save the prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SubHeader
        text="Admin Portal"
        buttons={[{ icon: <Web className="subheader-icon" />, onPress: () => navigate(-1) }]}
      />

      <div className="wrap">
        <Breadcrumbs leftTitle="Admin Portal" rightTitle="AI Prompt Management" route="/admin-portal" />

        <p className="instruction">AI Prompt for running the DQF AI Evaluation, click Edit to enable editing.</p>

        <textarea
          className="prompt-box"
          aria-label="DQF AI Evaluation prompt"
          value={body}
          readOnly={!editing}
          onChange={e => {
            setBody(e.target.value);
            if (error) setError('');
          }}
        />
        {error && <div className="field-error">{error}</div>}

        <div className="actions">
          <Button variant="outline" text="Edit" disabled={editing} onPress={() => setEditing(true)} />
          <Button variant="primary" text={saving ? 'Saving...' : 'Save'} disabled={saving} onPress={handleSave} />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AiPromptManagement;
